import styled from 'styled-components';
import { IoIosArrowDown as DownArrow } from "react-icons/io";
import { useState } from 'react';

const SectionContainer = styled.section`
  display: flex;
  width: 100%;
  max-width: 1120px;
  flex-direction: column;
  align-items: center;
  gap: 60px;
  margin: 0 auto;
  padding: 60px 20px;
  box-sizing: border-box;

  @media (max-width: 991px) {
    padding: 40px 20px;
  }
  @media (max-width: 640px) {
    padding: 43px 16px 49px;
    gap: 30px;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  width: 100%;
  max-width: 554px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  position: relative;
`;

const Subtitle = styled.p`
  align-self: stretch;
  color: #1c1b1f;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  font: 700 16px/120% Inter, sans-serif;
  margin: 0;
`;

const MainTitle = styled.h1`
  width: 100%;
  max-width: 554px;
  color: #1c1b1f;
  text-align: center;
  letter-spacing: -0.48px;
  position: relative;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) { 
    font-size: 36px;
    line-height: 44px;
  }
  @media (max-width: 640px) {
    font-size: 26px;
    line-height: 32px;
    letter-spacing: -0.28px;
  }
`;

const GridContainer = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  position: relative;
`;

const GridRow = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: stretch;
  text-align:center;
  justify-content: center;
  gap: 20px;
  position: relative;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    align-items: center;
    flex-direction: column;
  }
`;

const CardContainer = styled.article`
  display: flex;
  width: 100%;
  max-width: 360px;
  padding: 24px 20px;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  min-width: 300px;
  border-radius: 16px;
  border: 1px solid #f2f2f2;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  box-sizing: border-box;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(106, 55, 227, 0.85);
    transform: translateY(-4px);
  }

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }
  @media (max-width: 640px) {
    flex: 1;
    padding: 17px;
    width: 100%;
    min-width: unset;
    background-color: ${props => props.isOpen ? 'rgba(106, 55, 227, 0.85)' : '#fff'};
    
    &:hover {
      transform: none;
      background-color: ${props => props.isOpen ? 'rgba(106, 55, 227, 0.85)' : '#fff'};
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  width: 100%;
  max-width: 320px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-shrink: 0;
  position: relative;

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
  }
  @media (max-width: 640px) {
    flex: 1;
    gap: 0px;
    max-width: 350px;
    min-width: unset;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  text-align: center;
  justify-content: space-between;
  align-items: center;
  align-self: center;
  position: relative;

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
  }
  @media (max-width: 640px) {
    flex: 1;
    gap: 10px;
    align-items: center;
    flex-direction: row;
    min-width: unset;
  }
`;

const CardTitle = styled.h2`
  align-self: center;
  color: #1c1b1f;
  letter-spacing: -0.2px;
  position: relative;
  font: 600 20px/140% Inter, sans-serif;
  margin: 0;
  transition: color 0.3s ease;

  ${CardContainer}:hover & {
    color: #fff;
    @media (max-width: 640px) {
      color: ${props => props.isOpen ? '#fff' : '#1c1b1f'};
  }
  }

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }
  @media (max-width: 640px) {
    align-self: center;
    text-align: left;
    font-size: 16px;
    flex: 1;
    width: 100%;
    min-width: unset;
    color: ${props => props.isOpen ? '#fff' : '#1c1b1f'};
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  position: relative;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }
  @media (max-width: 640px) {
    flex: 1;
    width: 100%;
    text-align: left;
    min-width: unset;
    padding-left: 10px;
    max-height: ${props => props.isOpen ? '500px' : '0'};
    margin-top: ${props => props.isOpen ? '16px' : '0'};
    display: ${props => props.isOpen ? 'flex' : 'none'};
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  position: relative;

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }
  @media (max-width: 640px) {
    flex: 1;
    width: 100%;
    min-width: unset;
  }
`;

const FeatureText = styled.p`
  flex: 1 0 0;
  color: #64636a;
  letter-spacing: -0.08px;
  position: relative;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  transition: color 0.3s ease;

  ${CardContainer}:hover & {
    color: #fff;
  }

  @media (max-width: 991px) {
    flex: 1 1 calc(50% - 10px);
    min-width: 280px;
  }
  @media (max-width: 640px) {
    flex: 1;
    width: 100%;
    min-width: unset;
    color: ${props => props.isOpen ? '#fff' : '#64636a'};
  }
`;

const IconContainer = styled.div`
  width: 34px;
  height: 34px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(106, 55, 227, 0.1);
  transition: background-color 0.3s ease;
  padding: 10px;

  ${CardContainer}:hover & {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 640px) {
    width: 24px;
    height: 24px;
    background-color: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(106, 55, 227, 0.1)'};
    
  ${CardContainer}:hover & {
    background-color: ${props => props.isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(106, 55, 227, 0.1)'};
  }
  }
`;

const IconWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    transition: all 0.3s ease;
  }

  ${CardContainer}:hover & svg path {
      fill: #fff !important;
  }
  
  @media (max-width: 640px) {
    svg path {
      fill: ${props => props.isOpen ? 'white' : '#6A37E3'} !important;
    }
      
  ${CardContainer}:hover & svg path {
      fill: ${props => props.isOpen ? 'white' : '#6A37E3'} !important;
  }
  }
`;

const ArrowIcon = styled.div`
  display: none;
  @media (max-width: 640px) {
    display: block;
    position: absolute; 
    right: -6px;
  }
`;

const CardIcon = ({ iconSvg, isOpen }) => {
  return (
    <IconContainer isOpen={isOpen}>
      <IconWrapper isOpen={isOpen}>
        <div dangerouslySetInnerHTML={{ __html: iconSvg }} />
      </IconWrapper>
    </IconContainer>
  );
};

const FeatureCard = ({ title, features, iconSvg }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <CardContainer isOpen={isOpen} onClick={toggleAccordion}>
      <CardContent>
        <CardHeader>
          <CardIcon iconSvg={iconSvg} isOpen={isOpen} />
          <CardTitle isOpen={isOpen}>{title}</CardTitle>
          <ArrowIcon>
            <DownArrow 
              style={{
                width: 17, 
                height: 17,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: isOpen ? '#fff' : '#1c1b1f'
              }} 
            />
          </ArrowIcon>
        </CardHeader>
        <FeatureList isOpen={isOpen}>
          {features.map((feature, index) => (
            <FeatureItem key={index}>
              <FeatureText isOpen={isOpen}>{feature}</FeatureText>
            </FeatureItem>
          ))}
        </FeatureList>
      </CardContent>
    </CardContainer>
  );
};

export const WhyUsSection = () => {
  return (
    <SectionContainer>
      <HeaderContainer>
        <Subtitle>WHY US</Subtitle>
        <MainTitle>Smarter Conversations, Better Outcomes</MainTitle>
      </HeaderContainer>
      <FeatureGrid />
    </SectionContainer>
  );
};

const FeatureGrid = () => {
  const features = [
    {
      number: "001",
      title: "Improved Student Experience",
      features: [
        "Identifies pain points in the student journey.",
        "Highlights opportunities to enhance academic, social, and support services."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_678)">
          <path d="M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18ZM12 3L1 9L12 15L21 10.09V17H23V9L12 3Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_678">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    },
    {
      number: "002",
      title: "AI-Based Data-Driven Decision Making",
      features: [
        "Empowers administrators and faculty to make informed decisions backed by real feedback.",
        "Reduced guesswork in policy-making, marketing, and program design."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_691)">
          <path d="M21 8C19.55 8 18.74 9.44 19.07 10.51L15.52 14.07C15.22 13.98 14.78 13.98 14.48 14.07L11.93 11.52C12.27 10.45 11.46 9 10 9C8.55 9 7.73 10.44 8.07 11.52L3.51 16.07C2.44 15.74 1 16.55 1 18C1 19.1 1.9 20 3 20C4.45 20 5.26 18.56 4.93 17.49L9.48 12.93C9.78 13.02 10.22 13.02 10.52 12.93L13.07 15.48C12.73 16.55 13.54 18 15 18C16.45 18 17.27 16.56 16.93 15.48L20.49 11.93C21.56 12.26 23 11.45 23 10C23 8.9 22.1 8 21 8Z" fill="#6A37E3"></path>
          <path d="M15 9L15.94 6.93L18 6L15.94 5.07L15 3L14.08 5.07L12 6L14.08 6.93L15 9Z" fill="#6A37E3"></path>
          <path d="M3.5 11L4 9L6 8.5L4 8L3.5 6L3 8L1 8.5L3 9L3.5 11Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_691">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    },
    {
      number: "003",
      title: "Retention and Engagement",
      features: [
        "Early identification of issues affecting student satisfaction improves retention.",
        "Helps uncover reasons behind disengagement or dropout, allowing for targeted intervention."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_704)">
          <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_704">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    },
    {
      number: "004",
      title: "Competitive Advantage",
      features: [
        "Institutions that understand their students better help differentiate themselves in recruitment and branding.",
        "Enhances marketing by aligning messaging with what students value most."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_718)">
          <path d="M7.5 21H2V9H7.5V21ZM14.75 3H9.25V21H14.75V3ZM22 11H16.5V21H22V11Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_718">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    },
    {
      number: "005",
      title: "Program and Curriculum Optimization",
      features: [
        "Gather feedback on course offerings, teaching methods, and academic support.",
        "Feedback from students helps with revisions to curriculum and instructional design."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_731)">
          <path d="M19.1401 12.9399C19.1801 12.6399 19.2001 12.3299 19.2001 11.9999C19.2001 11.6799 19.1801 11.3599 19.1301 11.0599L21.1601 9.4799C21.3401 9.3399 21.3901 9.0699 21.2801 8.8699L19.3601 5.5499C19.2401 5.3299 18.9901 5.2599 18.7701 5.3299L16.3801 6.2899C15.8801 5.9099 15.3501 5.5899 14.7601 5.3499L14.4001 2.8099C14.3601 2.5699 14.1601 2.3999 13.9201 2.3999H10.0801C9.84011 2.3999 9.65011 2.5699 9.61011 2.8099L9.25011 5.3499C8.66011 5.5899 8.12011 5.9199 7.63011 6.2899L5.24011 5.3299C5.02011 5.2499 4.77011 5.3299 4.65011 5.5499L2.74011 8.8699C2.62011 9.0799 2.66011 9.3399 2.86011 9.4799L4.89011 11.0599C4.84011 11.3599 4.80011 11.6899 4.80011 11.9999C4.80011 12.3099 4.82011 12.6399 4.87011 12.9399L2.84011 14.5199C2.66011 14.6599 2.61011 14.9299 2.72011 15.1299L4.64011 18.4499C4.76011 18.6699 5.01011 18.7399 5.23011 18.6699L7.62011 17.7099C8.12011 18.0899 8.65011 18.4099 9.24011 18.6499L9.60011 21.1899C9.65011 21.4299 9.84011 21.5999 10.0801 21.5999H13.9201C14.1601 21.5999 14.3601 21.4299 14.3901 21.1899L14.7501 18.6499C15.3401 18.4099 15.8801 18.0899 16.3701 17.7099L18.7601 18.6699C18.9801 18.7499 19.2301 18.6699 19.3501 18.4499L21.2701 15.1299C21.3901 14.9099 21.3401 14.6599 21.1501 14.5199L19.1401 12.9399ZM12.0001 15.5999C10.0201 15.5999 8.40011 13.9799 8.40011 11.9999C8.40011 10.0199 10.0201 8.3999 12.0001 8.3999C13.9801 8.3999 15.6001 10.0199 15.6001 11.9999C15.6001 13.9799 13.9801 15.5999 12.0001 15.5999Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_731">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    },
    {
      number: "006",
      title: "Stakeholder Alignment",
      features: [
        "Helps align administrative priorities with student expectations and experiences.",
        "Creates a shared understanding among leadership, faculty, and support staff."
      ],
      iconSvg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_356_744)">
          <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM14 6H10V4H14V6Z" fill="#6A37E3"></path>
        </g>
        <defs>
          <clipPath id="clip0_356_744">
            <rect width="24" height="24" fill="white"></rect>
          </clipPath>
        </defs>
      </svg>`
    }
  ];

  return (
    <GridContainer>
      <GridRow>
        {features.slice(0, 3).map((feature, index) => (
          <FeatureCard
            key={index}
            title={feature.title}
            features={feature.features}
            iconSvg={feature.iconSvg}
          />
        ))}
        {features.slice(3, 6).map((feature, index) => (
          <FeatureCard
            key={index + 3}
            title={feature.title}
            features={feature.features}
            iconSvg={feature.iconSvg}
          />
        ))}
      </GridRow>
    </GridContainer>
  );
};

export default WhyUsSection;